import { motion } from "framer-motion";
import { MousePointer2, Phone } from "lucide-react";
import { EASE } from "@/lib/lenis";
import { Reveal } from "@/components/motion/Reveal";
import { SectionIntro } from "@/components/site/Bits";

const FastFigure = () => (
  <div className="flex h-full flex-col justify-center px-6">
    <div className="flex justify-between font-mono text-xs text-[#A1A1AA]">
      <span>Day 1</span>
      <span>Day 10</span>
    </div>
    <div className="relative mt-3 h-10 overflow-hidden rounded-md border border-white/10 bg-white/[0.02]">
      <div className="absolute inset-0 grid grid-cols-10">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className="border-r border-white/[0.06] last:border-r-0" />
        ))}
      </div>
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
        className="absolute inset-y-1.5 left-1.5 right-[8%] origin-left rounded bg-gradient-to-r from-[#2563EB] via-[#3B82F6] to-[#2DD4D4]"
      />
    </div>
    <div className="mt-3 flex justify-between text-xs text-white/80">
      <span>Mockup</span>
      <span>Build</span>
      <span className="text-[#2DD4D4]">Launch</span>
    </div>
  </div>
);

const LocalFigure = () => (
  <div className="relative grid h-full place-items-center">
    {[1, 2, 3].map((i) => (
      <span key={i} className="absolute rounded-full border border-white/10" style={{ width: i * 56, height: i * 56 }} />
    ))}
    <span className="radar-sweep absolute" style={{ width: 168, height: 168 }} />
    <span className="relative h-2.5 w-2.5 rounded-full bg-[#2DD4D4] shadow-[0_0_20px_4px_rgba(45,212,212,0.6)]" />
    <span className="absolute translate-y-6 font-mono text-xs text-white">Wayne, NJ</span>
    <span className="absolute bottom-4 right-4 font-mono text-xs text-[#A1A1AA]">~30 mi</span>
  </div>
);

const BusinessFigure = () => (
  <div className="relative grid h-full place-items-center">
    <div className="w-[78%] max-w-[240px] rounded-lg border border-white/10 bg-[#11141B] p-4">
      <div className="h-2 w-2/3 rounded bg-white/70" />
      <div className="mt-2 h-2 w-1/2 rounded bg-white/20" />
      <div className="mt-4 flex items-center gap-2">
        <span className="flex items-center gap-1.5 rounded-full bg-[#2F6BFF] px-3 py-1.5 text-xs font-medium">
          <Phone className="h-3 w-3" aria-hidden="true" /> Call now
        </span>
        <span className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Get a quote</span>
      </div>
    </div>
    <MousePointer2 className="float-bob absolute left-[46%] top-[62%] h-5 w-5 fill-white text-white" aria-hidden="true" />
  </div>
);

const FIGS = [
  {
    fig: "Fig 0.1",
    title: "Fast",
    lead: "Live in 7–10 days.",
    copy: "A clear plan and a short timeline. You see a mockup first, then we build and launch without months of back-and-forth.",
    Visual: FastFigure,
  },
  {
    fig: "Fig 0.2",
    title: "Local",
    lead: "Three founders in Wayne, NJ.",
    copy: "We're nearby, easy to reach, and happy to meet in person. We work with businesses within about 30 miles.",
    Visual: LocalFigure,
  },
  {
    fig: "Fig 0.3",
    title: "Built to bring in business",
    lead: "Made for calls, not just compliments.",
    copy: "Tap-to-call buttons, simple quote forms, and pages that work great on phones, so visitors turn into customers.",
    Visual: BusinessFigure,
  },
];

export const ValueFigs = () => (
  <section className="section" data-testid="value-section">
    <div className="container-x">
      <SectionIntro
        eyebrow="Why ATA Forge"
        title={["A better website,", "without the runaround."]}
        copy="Most small businesses don't need a giant agency. They need a site that looks sharp, works on phones, and gets the phone ringing."
        testId="value-heading"
      />
      <div className="mt-16 grid divide-y divide-white/[0.08] border-y border-white/[0.08] md:grid-cols-3 md:divide-x md:divide-y-0">
        {FIGS.map(({ fig, title, lead, copy, Visual }, i) => (
          <Reveal key={fig} delay={i * 0.1} className="py-10 md:px-8 md:first:pl-0 md:last:pr-0 lg:px-10" data-testid={`value-fig-${i + 1}`}>
            <div className="h-48 overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.015]">
              <Visual />
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-[#2DD4D4]">{fig}</p>
            <h3 className="mt-3 text-2xl font-semibold tracking-[-0.03em]">{title}</h3>
            <p className="mt-2 font-medium text-white/90">{lead}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-[#A1A1AA]">{copy}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
